import {Translator} from '@logistics-frontend/core/i18n'
import {type Optional} from '@logistics-frontend/types'
import {isValidEmail} from '@logistics-frontend/utils'
import {array, object, string} from 'yup'

import {appConfig, i18n} from 'src/shared/config'
import {DeliveryType, Order} from 'src/shared/types'
import {OfferDeliveryType} from 'src/widgets/desktop'
import {OrderParcelSize} from 'src/widgets/Parcel/consts'

import {isEmptyOrUndefined} from './utils/is-empty-or-undefined'

const getDeliveryTypeSchema = (t: Translator) =>
  string().oneOf([DeliveryType.NDD, DeliveryType.SDD, DeliveryType.Express]).required(t('validation.required'))

const getPickUpDropOffPointSchema = () =>
  object().shape({
    city: object().optional().required(i18n('validation.required')),
    point: object().optional().required(i18n('validation.required')),
  })

const NddContactSchema = object().shape({
  phone: string().test('phone', i18n('validation.phone.invalid'), appConfig.checkIsValidPhone),
  name: string().required(i18n('validation.required')),
})

export function getEmailInputSchema(t: Translator) {
  return string()
    .email(t('claim.form.validation.email'))
    .test('is-valid', t('claim.form.validation.email'), function testEmailInput(value = '') {
      return isEmptyOrUndefined(value) || isValidEmail(value)
    })
}

const ContactSchema = object().shape({
  phone: string()
    .required(i18n('validation.required'))
    .test('phone', i18n('validation.phone.invalid'), appConfig.checkIsValidPhone),
  email: getEmailInputSchema(i18n),
})

const AddressSchema = object().shape({
  fullName: string().required(i18n('validation.required')),
})

const getProductSchema = () =>
  object().shape({
    name: string().required(i18n('validation.required')),
  })

const getPaymentMethodSchema = (t: Translator) =>
  object().shape({
    id: string().required(t('validation.required')),
  })

const getVerticalSchema = (t: Translator) =>
  object().shape({
    name: string().required(t('claim.form.validation.required')),
  })

const getParcelSizeSchema = () =>
  string()
    .oneOf([
      OrderParcelSize.ExtraSmall,
      OrderParcelSize.Small,
      OrderParcelSize.Medium,
      OrderParcelSize.Large,
      OrderParcelSize.ExtraLarge,
    ])
    .required(i18n('validation.required'))

const getOfferSchema = (t: Translator) =>
  object().shape({
    id: string().required(t('claim.form.validation.required')),
  })

const getOfferFlowDeliveryTypeSchema = () =>
  string().optional().default(undefined).notRequired().oneOf([OfferDeliveryType.Now, OfferDeliveryType.ToTime])

const NddDestinationPointSchema = object().shape({
  pickUpPoint: getPickUpDropOffPointSchema(),
  contact: NddContactSchema,
  parcels: array()
    .min(1)
    .of(
      object().shape({
        size: getParcelSizeSchema(),
        products: array().min(1).of(getProductSchema()),
      }),
    ),
})

/**
 * Схема валидации формы заказа
 * Не уверен, что сейчас уместно разделять
 * эту схему на маленькие блоки так как предстоит рефакторинг этой формы
 */
function getFormValidationSchema(t: Translator) {
  return object({
    deliveryType: getDeliveryTypeSchema(t),
    sourcePoint: object().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema =>
        schema.shape({
          dropOffPoint: getPickUpDropOffPointSchema(),
          contact: object().shape({
            phone: string()
              .optional()
              .notRequired()
              .test('phone', i18n('validation.phone.invalid'), value => {
                if (value) {
                  return appConfig.checkIsValidPhone(value)
                }

                return true
              }),
          }),
        }),
      otherwise: schema =>
        schema.shape({
          address: AddressSchema,
          contact: ContactSchema,
        }),
    }),
    destinationPoints: array().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema => schema.min(1).of(NddDestinationPointSchema),
      otherwise: schema =>
        schema.min(1).of(
          object().shape({
            address: AddressSchema,
            contact: ContactSchema,
            parcels: array()
              .min(1)
              .of(
                object().shape({
                  products: array().min(1).of(getProductSchema()),
                }),
              ),
          }),
        ),
    }),
    ndd: object().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema =>
        schema.shape({
          deliveryDate: string().required(t('claim.form.validation.required')),
        }),
      otherwise: schema => schema.optional(),
    }),
    nddOrder: object().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema =>
        schema.shape({
          id: string().required(t('claim.form.validation.required')),
          price: string().required(t('claim.form.validation.required')),
          totalPrice: string().required(t('claim.form.validation.required')),
        }),
      otherwise: schema => schema.optional(),
    }),
    paymentMethod: getPaymentMethodSchema(t),
    offerFlow: object().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema => schema.optional(),
      otherwise: schema =>
        schema.shape({
          deliveryType: getOfferFlowDeliveryTypeSchema(),
          offer: getOfferSchema(t),
          deliveryTime: string()
            .optional()
            .default(undefined)
            .when('deliveryType', {
              is: (deliveryType?: OfferDeliveryType) => deliveryType === OfferDeliveryType.ToTime,
              then: schema => schema.required(t('claim.form.validation.required')),
              otherwise: schema => schema.notRequired(),
            }),
          vertical: getVerticalSchema(t),
          deliveryInterval: object().test(
            'deliveryInterval',
            t('claim.form.validation.required'),
            // @ts-expect-error - тип value не совпадает с типом валидатора
            function validateDeliveryInterval(value: Optional<Order['offerFlow']['deliveryInterval']>, context) {
              // TODO фиксить здесь https://st.yandex-team.ru/CARGODEV-23436
              // Это грязный хак, который подглядел здесь https://github.com/jquense/yup/issues/735
              // when не работает со свойством, которое находится выше по дереву, от этого и идет невозможность привязки к offerFlow.vertical.name
              // @ts-expect-error - from существует в context
              const isNightVertical = context.from[2]?.value?.offerFlow?.vertical?.name === DeliveryType.Night

              return isNightVertical ? !!value : true
            },
          ),
        }),
    }),
  })
}

export const FormSchema = getFormValidationSchema(i18n)
